/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { useEditRemoveSOP } from '../hooks/useEditRemoveSOP'
import {
  Column,
  useFlexLayout,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import { FixedSizeList, areEqual } from 'react-window'
import { useOnClickOutside } from 'usehooks-ts'

import { ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons'
import {
  Box,
  Table,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
  chakra,
} from '@chakra-ui/react'
import memoize from 'memoize-one'

import { Navigation, PageSizeSelector, TableSpinner } from '@/components/ui'
import { EditSOPModal, RemoveSOPModal } from '@/features/sop-crud'
import { StandardOperatingProcedureFilterInput } from '@/graphql/generated/schemas'
import { getCurrentRangeMessage } from '@/utils/pageNavigation'

import { RefetchSOPFnType } from '../types/types'
import { getOrderBy, getSortedColumn } from '../utils/query'

interface RenderRowIProps {
  data: {
    handleSelectItem: any
    items: {
      getRowProps: any
      cells: any
      original: {
        id: string
        name: string
        displayId: string
      }
    }[]
  }
  index: number
  style: any
}

const createItemData = memoize((items, handleSelectItem) => ({
  items,
  handleSelectItem,
}))

const ROW_HEIGHT = 40

export type DataTableProps<DataT extends unknown> = {
  data: DataT[]
  columns: Column<DataT>[]
  refetch?: RefetchSOPFnType
  total?: number
  filters?: StandardOperatingProcedureFilterInput
  onItemClick?: (id: string) => void
  pageSize?: number
  onPageResize?: (pageIndex: number) => void
}

export const DataTable = <DataT extends unknown>({
  data,
  columns,
  refetch,
  total,
  filters,
  onItemClick,
  pageSize: initialPageSize,
  onPageResize,
}: DataTableProps<DataT>) => {
  const ref = useRef(null)
  const listRef = useRef(null)

  const defaultColumn = useMemo(
    () => ({
      // When using the useFlexLayout:
      minWidth: 30, // minWidth is only used as a limit for resizing
      width: 80, // width is used for both the flex-basis and flex-grow
      maxWidth: 200, // maxWidth is only used as a limit for resizing
    }),
    []
  )
  const [selectedId, setSelectedId] = useState(null)
  const skipPageResetRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)

  const {
    getTableProps,
    headerGroups,
    rows,
    columns: tableColumns,
    prepareRow,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      autoResetPage: !skipPageResetRef.current,
      columns,
      data,
      defaultColumn,
      initialState: {
        pageIndex: 0,
        pageSize: initialPageSize,
        sortBy: [
          {
            id: 'date',
            desc: true,
          },
        ],
      },
      manualPagination: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      pageCount: -1,
    },
    useFlexLayout,
    useSortBy,
    usePagination
  )
  const pageCount = Math.ceil(total / pageSize)
  const sortedColumn = getSortedColumn(tableColumns)

  useEffect(() => {
    skipPageResetRef.current = true
    setIsLoading(true)
    refetch({
      filter: filters,
      first: pageIndex,
      last: pageSize,
      orderBy: getOrderBy(sortedColumn),
    }).then(() => {
      setIsLoading(false)
      skipPageResetRef.current = false
    })
  }, [pageIndex, pageSize, JSON.stringify(sortedColumn)])

  useEffect(() => {
    gotoPage(0)
    skipPageResetRef.current = true
    setIsLoading(true)
    refetch({
      filter: filters,
      first: pageIndex,
      last: pageSize,
      orderBy: getOrderBy(sortedColumn),
    }).then(() => {
      setIsLoading(false)
      skipPageResetRef.current = false
    })
  }, [JSON.stringify(filters)])

  const handleSelectItem = (id: string) => {
    setSelectedId(id)
    onItemClick?.(id)
  }

  useOnClickOutside(ref, () => {
    if (selectedId) {
      setSelectedId(null)
    }
  })

  const itemData = createItemData(rows, handleSelectItem)

  const {
    sop,
    isEditSOPOpen,
    isRemoveSOPOpen,
    onEditSOPClose,
    onRemoveSOPClose,
    onSOPEdit,
    onSOPRemove,
  } = useEditRemoveSOP()

  // eslint-disable-next-line react/no-unstable-nested-components
  const RenderRow = memo<RenderRowIProps>(
    ({ data, index, style }: RenderRowIProps) => {
      const { items, handleSelectItem } = data
      const row = items[index]
      prepareRow(row)
      const isSelected = selectedId === row.original.id

      return (
        <Tr
          {...row.getRowProps({
            style,
          })}
          _hover={{
            bgColor: isSelected ? '#4284f8' : 'gray.100',
          }}
          bgColor={isSelected ? '#6096f5' : '#fff'}
          cursor='pointer'
          h='40px'
          key={index}
          onClick={() =>
            handleSelectItem(row.original.id, row.original.displayId)
          }
          transition='ease-in-out'
          transitionDuration='300ms'
          transitionProperty='all'
        >
          {row.cells.map((cell) => (
            <Td
              {...cell.getCellProps()}
              alignItems='center'
              borderColor='#D5DCE4'
              color={isSelected ? '#fff' : '#2D2E41'}
              flexDirection='row'
              fontSize='14px'
              fontWeight='medium'
              isNumeric={cell.column.isNumeric}
              key={index}
              letterSpacing='-0.53px'
              pos='relative'
              px='4'
              py='0'
            >
              <Box pos='relative' top='50%' transform='translateY(-50%)'>
                {cell.render('Cell', {
                  onEdit: () => onSOPEdit(data.items[index].original),
                  onRemove: () => onSOPRemove(data.items[index].original),
                  refetch: refetch,
                })}
              </Box>
            </Td>
          ))}
        </Tr>
      )
    },
    areEqual
  )

  return (
    <Box mb='50px' w={{ base: '1000px', lg: 'full' }}>
      <Table
        {...getTableProps()}
        bgColor='white'
        border='none'
        borderWidth='3px'
        data-testid='sopsPage_table'
        ref={listRef}
        rounded={{ base: 'none', lg: 'lg' }}
        shadow='0px 2px 5px 6px rgba(0, 0, 0, 0.1)'
      >
        <Thead
          bgColor='#F2F4F9'
          data-testid='sopsPage_table_header'
          overflowX='hidden'
          overflowY='auto'
        >
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps()}
                  alignItems='center'
                  borderColor='#D5DCE4'
                  color='#2D2E41'
                  d='flex'
                  fontSize='14px'
                  fontWeight='semibold'
                  isNumeric={column?.isNumeric}
                  key={index}
                  letterSpacing='-1px'
                  px='4'
                  textTransform='capitalize'
                >
                  <Box d='inline-block' {...column?.getSortByToggleProps()}>
                    {column.render('Header')}
                  </Box>
                  <chakra.span>
                    {column?.isSorted ? (
                      column?.isSortedDesc ? (
                        <ArrowDownIcon aria-label='sorted descending' />
                      ) : (
                        <ArrowUpIcon aria-label='sorted ascending' />
                      )
                    ) : null}
                  </chakra.span>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody ref={ref}>
          {isLoading ? (
            <TableSpinner height={data.length * ROW_HEIGHT} />
          ) : (
            <FixedSizeList
              height={data.length * ROW_HEIGHT}
              itemCount={rows.length}
              itemData={itemData}
              itemSize={ROW_HEIGHT}
            >
              {RenderRow}
            </FixedSizeList>
          )}
        </Tbody>
        <Tfoot bgColor='#F2F4F9'>
          <Tr>
            <Td
              alignItems='center'
              color='#2D2E41'
              display='grid'
              fontSize='14px'
              fontWeight='semibold'
              gridTemplateColumns='repeat(3, 1fr)'
              letterSpacing='-1px'
              textTransform='none'
            >
              <Box as='p'>
                {getCurrentRangeMessage(pageIndex, pageSize, total)}
              </Box>
              <Navigation
                dataTestId='sopsPage_table_navigation'
                gotoPage={gotoPage}
                nextPage={nextPage}
                pageCount={pageCount}
                pageIndex={pageIndex}
                previousPage={previousPage}
              />
              <PageSizeSelector
                dataTestId='sopsPage_table_pageSize'
                onClick={onPageResize}
                pageSize={pageSize}
                setPageSize={setPageSize}
                title='SOPs per page:'
              />
            </Td>
          </Tr>
        </Tfoot>
      </Table>
      {isRemoveSOPOpen && (
        <RemoveSOPModal
          isOpen={isRemoveSOPOpen}
          onClose={onRemoveSOPClose}
          refetch={refetch}
          sop={sop}
        />
      )}
      {isEditSOPOpen && (
        <EditSOPModal
          isOpen={isEditSOPOpen}
          onClose={onEditSOPClose}
          refetch={refetch}
          sop={sop}
        />
      )}
    </Box>
  )
}
